import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useTheme } from "../App";
import { useAuth } from "../lib/AuthContext";
import { Button } from "./ui/button";
import { Users, Settings, Sun, Moon, Clock, Calendar, CalendarDays, BarChart3, LogOut, AlertTriangle, Building2, Lock, Sparkles, ArrowRight, Check, Loader2, CreditCard, HelpCircle, User, FileText, PartyPopper, Copy, Coffee } from "lucide-react";
import { cn } from "../lib/utils";
import { useState, useEffect } from "react";
import { businessHoursApi } from "../lib/api";
import api from "../lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { RotalieLogo } from "./RotalieLogo";
import SubscriptionBlocker from "./SubscriptionBlocker";

// Copy to clipboard helper
const copyToClipboard = async (text, label = "Copied!") => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch (err) {
    toast.error("Failed to copy");
  }
};

// Feature metadata for UI display
const FEATURE_METADATA = {
  dayView: { name: 'Day View', minTier: 'light' },
  weekView: { name: 'Week View', minTier: 'light' },
  basicReports: { name: 'Basic Reports', minTier: 'light' },
  advancedReports: { name: 'Advanced Reports', minTier: 'standard' },
  wageCostCalculations: { name: 'Wage Cost Calculations', minTier: 'standard' },
  breakManagement: { name: 'Break Management', minTier: 'standard' },
  overtimeRates: { name: 'Overtime & Custom Rates', minTier: 'standard' },
  absenceTracking: { name: 'Absence & Leave Tracking', minTier: 'standard' },
  eventManagement: { name: 'Event Management', minTier: 'standard' },
  clockInOut: { name: 'Clock In/Out', minTier: 'professional', comingSoon: true },
  payrollIntegrations: { name: 'Payroll Integrations', minTier: 'professional', comingSoon: true },
  csvExport: { name: 'CSV Export', minTier: 'standard' },
  pdfExport: { name: 'PDF Export', minTier: 'standard' }
};

const isFeatureComingSoon = (feature) => FEATURE_METADATA[feature]?.comingSoon === true;

// Upgrade Modal Component for Nav Items
const UpgradeModal = ({ open, onClose, feature, currentTier, subscription }) => {
  const [loading, setLoading] = useState(false);
  const featureMeta = FEATURE_METADATA[feature];
  const requiredTier = featureMeta?.minTier || 'standard';
  const requiredTierConfig = subscription?.available_tiers?.[requiredTier] || { name: requiredTier, price: 29 };
  const isComingSoon = isFeatureComingSoon(feature);

  const getPackageId = (tier) => {
    const packageMap = {
      'light': 'light_monthly',
      'standard': 'standard_monthly',
      'professional': 'professional_monthly'
    };
    return packageMap[tier] || 'standard_monthly';
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const packageId = getPackageId(requiredTier);
      const response = await api.post('/checkout/create-session', {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          {isComingSoon ? (
            <>
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <DialogTitle className="text-center">Coming Soon</DialogTitle>
              <DialogDescription className="text-center">
                <span className="font-medium text-foreground">{featureMeta?.name}</span> is currently in development and will be available soon on the Professional plan.
              </DialogDescription>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <DialogTitle className="text-center">Upgrade to {requiredTierConfig?.name}</DialogTitle>
              <DialogDescription className="text-center">
                <span className="font-medium text-foreground">{featureMeta?.name}</span> requires the {requiredTierConfig?.name} plan or higher.
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        {!isComingSoon && (
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">{requiredTierConfig?.name} Plan</span>
                <span className="text-2xl font-bold">£{requiredTierConfig?.price}<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
              </div>
              <ul className="space-y-2">
                {requiredTierConfig?.feature_flags && Object.entries(requiredTierConfig.feature_flags)
                  .filter(([_, v]) => v === true)
                  .slice(0, 6)
                  .map(([feat]) => (
                    <li key={feat} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-muted-foreground">
                        {FEATURE_METADATA[feat]?.name || feat.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>

            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700" 
              size="lg"
              onClick={handleUpgrade}
              disabled={loading}
              data-testid="layout-upgrade-now-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Upgrade Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            
            <p className="text-center text-xs text-muted-foreground">
              30-day money-back guarantee • Cancel anytime
            </p>
          </div>
        )}

        {isComingSoon && (
          <div className="py-4">
            <Button variant="outline" className="w-full" onClick={() => onClose(false)}>
              Got it
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Layout = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, company, logout, trialDaysRemaining, isCompanyAdmin, subscriptionTier, subscription, hasFeatureAccess, hasPermission, permissions } = useAuth();
  const navigate = useNavigate();
  const [shiftPatternsEnabled, setShiftPatternsEnabled] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, feature: null });

  // Get current tier for feature checking
  const currentTier = subscriptionTier || 'light';
  
  // Role-based permission checks using new granular permissions
  const canViewBilling = isCompanyAdmin || hasPermission('billing_view');
  const canManageSettings = isCompanyAdmin || hasPermission('settings_view') || hasPermission('settings_edit');
  const canViewEmployees = isCompanyAdmin || hasPermission('employees_view');
  const canViewShifts = isCompanyAdmin || hasPermission('shifts_view');
  const canViewAbsences = isCompanyAdmin || hasPermission('absences_view');
  const canViewEvents = isCompanyAdmin || hasPermission('events_view');
  const canViewReports = isCompanyAdmin || hasPermission('reports_view');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await businessHoursApi.get();
        setShiftPatternsEnabled(res.data.shift_patterns_enabled || false);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
    
    // Listen for settings changes
    const handleSettingsChange = () => fetchSettings();
    window.addEventListener('settings-updated', handleSettingsChange);
    return () => window.removeEventListener('settings-updated', handleSettingsChange);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Check feature access for nav items using dynamic flags from context
  const hasAbsenceAccess = hasFeatureAccess('absenceTracking');
  const hasAdvancedReportsAccess = hasFeatureAccess('advancedReports');
  const hasEventAccess = hasFeatureAccess('eventManagement');
  const hasBreakAccess = hasFeatureAccess('breakManagement');

  // Navigation items ordered by frequency of use
  const navItems = [
    { to: "/dashboard", icon: Calendar, label: "Rota", show: true, feature: null },
    { to: "/dashboard/absences", icon: CalendarDays, label: "Absences", show: canViewAbsences, feature: 'absenceTracking', locked: !hasAbsenceAccess },
    { to: "/dashboard/shifts", icon: Clock, label: "Shifts", show: shiftPatternsEnabled && canViewShifts, feature: null },
    { to: "/dashboard/breaks", icon: Coffee, label: "Breaks", show: canManageSettings, feature: 'breakManagement', locked: !hasBreakAccess },
    { to: "/dashboard/events", icon: PartyPopper, label: "Events", show: canViewEvents, feature: 'eventManagement', locked: !hasEventAccess },
    { to: "/dashboard/business-hours", icon: CalendarDays, label: "Open Hours", show: canManageSettings, feature: null },
    { to: "/dashboard/employees", icon: Users, label: "Employees", show: canViewEmployees, feature: null },
    { to: "/dashboard/reports", icon: BarChart3, label: "Reports", show: canViewReports, feature: null },
    { to: "/dashboard/settings", icon: Settings, label: "Settings", show: canManageSettings, feature: null },
  ].filter(item => item.show);

  // Always show trial banner for trial users
  const isOnTrial = company?.subscription_status === "trial";
  const trialCritical = trialDaysRemaining <= 3;

  const handleTrialUpgrade = () => {
    navigate("/dashboard/billing");
  };

  return (
    <SubscriptionBlocker>
      <div className="min-h-screen bg-background">
        {/* Trial Banner - Always visible for trial users */}
        {isOnTrial && (
          <div className={cn(
            "px-4 py-3 text-center border-b",
            trialCritical 
              ? "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900" 
              : "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900"
          )}>
            <div className="max-w-[1600px] mx-auto flex items-center justify-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {trialDaysRemaining > 0 
                    ? `You're on a free trial — ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} remaining`
                    : "Your free trial has expired"
                  }
                </span>
              </div>
              <Button 
                size="sm" 
                className={cn(
                  "h-7 px-3 text-xs font-semibold",
                  trialCritical 
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-amber-600 hover:bg-amber-700 text-white"
                )}
                onClick={handleTrialUpgrade}
                data-testid="trial-upgrade-btn"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                Upgrade Now
              </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <RotalieLogo showText={false} />
              <div>
                <h1 className="text-xl font-bold tracking-tight font-['Inter']" data-testid="app-title">Rotalie</h1>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  {company?.name || "Staff Scheduling Made Simple"}
                </p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                // If feature is locked, show grayed out button
                if (item.locked) {
                  return (
                    <button
                      key={item.to}
                      onClick={() => setUpgradeModal({ open: true, feature: item.feature })}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground/50 hover:text-muted-foreground hover:bg-accent/50 transition-all duration-200 cursor-pointer"
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{item.label}</span>
                      <Lock className="w-3 h-3 ml-1" />
                    </button>
                  );
                }
                
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/dashboard"}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent"
                      )
                    }
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{item.label}</span>
                  </NavLink>
                );
              })}
              
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="ml-2"
                data-testid="theme-toggle"
                aria-label="Toggle theme"
              >
                {theme === "light" ? (
                  <Moon className="w-5 h-5" />
                ) : (
                  <Sun className="w-5 h-5" />
                )}
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="ml-1" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center border border-border">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{user?.name || user?.email}</span>
                      <span className="text-xs text-muted-foreground font-normal capitalize">
                        {isCompanyAdmin ? "Company Admin" : (permissions?.role?.replace('_', ' ') || user?.role || 'Staff')}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {company && (
                    <>
                      <DropdownMenuItem 
                        className="cursor-pointer"
                        onClick={() => copyToClipboard(company.company_code, "Company Code copied!")}
                      >
                        <Building2 className="w-4 h-4 mr-2" />
                        <span className="text-xs flex-1">Code: <code className="font-mono bg-muted px-1 rounded">{company.company_code}</code></span>
                        <Copy className="w-3 h-3 ml-2 text-muted-foreground" />
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/account" className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      My Account
                    </Link>
                  </DropdownMenuItem>
                  {canViewBilling && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/billing" className="flex items-center">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Billing
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/documentation" className="flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Documentation
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto p-4 md:p-8">
        <Outlet />
      </main>

      {/* Upgrade Modal */}
      <UpgradeModal
        open={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, feature: null })}
        feature={upgradeModal.feature}
        currentTier={subscriptionTier}
        subscription={subscription}
      />
      </div>
    </SubscriptionBlocker>
  );
};

export default Layout;
